import { useState, useMemo, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as SolidIcons from "@fortawesome/free-solid-svg-icons";
import * as RegularIcons from "@fortawesome/free-regular-svg-icons";
import * as BrandIcons from "@fortawesome/free-brands-svg-icons";
import {
  faChevronDown,
  faChevronUp,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./IconPickSelector.module.css";
import { useLanguage } from "../../context/LanguageContext";

interface IconSelectorProps {
  value: string;
  onChange: (iconName: string) => void;
}

export default function IconPickSelector({ value, onChange }: IconSelectorProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<"solid" | "regular" | "brands">("solid");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownBoxRef = useRef<HTMLDivElement>(null);
  const [openUp, setOpenUp] = useState(false);

  const iconSets = useMemo(
    () => ({
      solid: SolidIcons,
      regular: RegularIcons,
      brands: BrandIcons,
    }),
    []
  );

  const icons = useMemo(() => {
    const lib = iconSets[category];
    return Object.keys(lib)
      .filter((key) => key.startsWith("fa"))
      .map((key) => ({
        name: key,
        icon: (lib as any)[key],
      }));
  }, [category, iconSets]);

  const filtered = useMemo(
    () =>
      icons.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase())
      ),
    [icons, search]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 320;
      setOpenUp(rect.bottom + dropdownHeight > viewportHeight);
    }
  }, [isOpen]);

  const selectedIcon =
    (SolidIcons as any)[value] ||
    (RegularIcons as any)[value] ||
    (BrandIcons as any)[value];

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <div className={styles.selector}>
        <span className={styles.title}>{t("chooseIcon")}</span>

        <div className={styles.selector}>
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              if (!isOpen && dropdownRef.current) {
                const rect = dropdownRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const dropdownHeight = 320;
                setOpenUp(rect.bottom + dropdownHeight > viewportHeight);
              }
              setIsOpen(!isOpen);
            }}
          >
            <span className={styles.text}>
              {value ? value.replace("fa", "") : t("selectIcon")}
            </span>
            <FontAwesomeIcon
              icon={isOpen ? faChevronUp : faChevronDown}
              className={styles.arrow}
            />
          </button>
        </div>

        {isOpen && (
          <div
            ref={dropdownBoxRef}
            className={`${styles.dropdown} ${openUp ? styles.openUp : ""}`}
            style={{
              top: openUp ? "auto" : "60%",
              bottom: openUp ? "60%" : "auto",
            }}
          >
            <div className={styles.categoryTabs}>
              {["solid", "regular", "brands"].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`${styles.tab} ${category === cat ? styles.activeTab : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCategory(cat as any);
                    setSearch("");
                  }}
                >
                  {t(cat)}
                </button>
              ))}
            </div>

            <div className={styles.searchBar}>
              <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
              <input
                type="text"
                placeholder={t("searchIcon")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className={styles.iconGrid}>
              {filtered.slice(0, 300).map((item) => (
                <div
                  key={item.name}
                  className={`${styles.iconItem} ${
                    value === item.name ? styles.active : ""
                  }`}
                  onClick={() => {
                    onChange(item.name);
                    setIsOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={item.icon} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.preview}>
        {selectedIcon ? (
          <FontAwesomeIcon icon={selectedIcon} className={styles.previewIcon} />
        ) : (
          <span className={styles.noIcon}>—</span>
        )}
      </div>
    </div>
  );
}
